import cx from "classnames";
import { useCustomClassContext } from "../../../hooks";
import {
  SummaryItemLarge,
  SummaryItemSmall,
} from "../../Block/SummaryItem/SummaryItem";
import BreadCrumbs from "../../Element/BreadCrumb";
import Divider from "../../Element/Divider/divider";
import ResponsiveImage from "../../Element/ResponsiveImage";
import * as Typography from "../../Element/Typography";
import { HeadingElement } from "../../Element/Typography/Heading/Heading";
import { BaseTeaserData, ImageCrop } from "../../Utility/dataTypes";
import { placeholderCrops } from "../../Utility/placeholder";
import { VariantName, variantConfigMap } from "./cardConfig";
import styles from "./index.module.css";

type Props = BaseTeaserData & {
  variant: VariantName;
  mobileUpVariant: VariantName;
  aspectRatio?: number;
  mobileUpAspectRatio?: number;
  CustomHeadingElement?: HeadingElement;
  dateAfter?: boolean;
  dateFormat?: string;
  displayPublishDate?: boolean;
  description?: string;
  descriptionInTitle?: boolean;
  // TODO: displayWidth and mobileUpDisplayWidth props are not implemented yet
  displayWidth?: number;
  mobileUpDisplayWidth?: number;
  displayPlayIcon?: boolean;
  imageSize?: "big" | "small";
  landscapeImageWidth?: number;
  portraitImageWidth?: number;
  placeholders?: {
    landscape: ImageCrop;
    portrait: ImageCrop;
    square: ImageCrop;
  };
  skipImage?: boolean;
  hideVerticalLine?: boolean;
};

const { Body, DateTime } = Typography;

const Card = ({
  id,
  path,
  image,
  title,
  sections,
  publishDate,
  variant,
  mobileUpVariant,
  aspectRatio,
  mobileUpAspectRatio,
  CustomHeadingElement,
  dateAfter,
  dateFormat = "dd mname yyyy",
  displayPublishDate = true,
  description,
  descriptionInTitle = false,
  displayPlayIcon,
  imageSize = "big",
  landscapeImageWidth = 630,
  portraitImageWidth = 300,
  placeholders = placeholderCrops,
  skipImage,
  tags,
  hideVerticalLine,
}: Props) => {
  const customClass = useCustomClassContext();
  const dateElement = displayPublishDate && !dateAfter && publishDate && (
    <DateTime date={new Date(publishDate)} format={dateFormat} />
  );

  const isCorrectVariantName = (value?: unknown): value is VariantName => {
    return (
      !!value &&
      typeof value === "string" &&
      Object.prototype.hasOwnProperty.call(variantConfigMap, value)
    );
  };

  const getVariantConfig = (
    variant: Props["variant"],
    mobileUpVariant: Props["mobileUpVariant"],
  ) => {
    if (
      isCorrectVariantName(variant) &&
      isCorrectVariantName(mobileUpVariant)
    ) {
      const variantConfig = variantConfigMap[variant];
      const mobileUpVariantConfig = variantConfigMap[mobileUpVariant];

      return {
        mode: mobileUpVariantConfig.mode,
        mobileMode: variantConfig.mode,
        direction: variantConfig.direction,
        mobileUpDirection: mobileUpVariantConfig.direction,
        isAnchorBlock: variantConfig.isAnchorBlock,
        aspectRatio: variantConfig.aspectRatio,
        mobileUpAspectRatio: mobileUpVariantConfig.aspectRatio,
      };
    }
    console.error(`Invalid variant name: ${variant} or ${mobileUpVariant}`);
    return;
  };

  const variantConfig = getVariantConfig(variant, mobileUpVariant);
  if (!variantConfig) {
    return;
  }

  const modeCrop = image?.crops[variantConfig.mode];
  const originalCrop = image?.crops.original;
  const mobileCrop = image?.crops[variantConfig.mobileMode];

  // Needs to respect mobile mode when selecting placeholder if mobileMode is NOT original
  const placeHolderImageMobile =
    variantConfig.mobileMode !== "original" &&
    variantConfig.mobileMode !== "square_30_26" &&
    placeholders[variantConfig.mobileMode];
  const placeHolderImage =
    variantConfig.mode === "original" || variantConfig.mode === "square_30_26"
      ? placeholders.portrait
      : placeholders[variantConfig.mode];

  const mobileImage =
    mobileCrop || modeCrop || placeHolderImageMobile || placeHolderImage;
  const mUpImage = modeCrop || placeHolderImage;

  if (!originalCrop || !modeCrop) {
    console.warn(`Missing Teaser Image Crops:`, {
      storyCueId: id,
      availableCrops: Object.keys(image?.crops || {}).join(" ") || "none",
      requested: [variantConfig.mode, "original"].join(" "),
      path,
    });
  }
  return (
    <div
      className={cx(
        styles.container,
        styles[imageSize],
        customClass?.latestArticle?.container,
        variantConfig.isAnchorBlock && styles.anchorblock,
        styles[`variant-${variant}`],
        styles[`variant-${mobileUpVariant}`],
      )}
      style={
        {
          "--card-mobile-up-direction": variantConfig.mobileUpDirection,
          "--card-direction": variantConfig.direction,
        } as React.CSSProperties
      }
    >
      {!skipImage && (
        <a href={path} className={cx(styles.image)}>
          <ResponsiveImage
            {...mobileImage}
            displayWidth={280}
            loading="lazy"
            fillAspectRatio={variantConfig.aspectRatio ?? aspectRatio}
            mobileUp={{
              ...mUpImage,
              displayWidth:
                variantConfig.mode === "portrait"
                  ? portraitImageWidth
                  : landscapeImageWidth,
              fill: "cover",
              fillPosition:
                variantConfig.mode === "portrait" ? "center top" : undefined,
              fillAspectRatio:
                variantConfig.mobileUpAspectRatio ?? mobileUpAspectRatio,
            }}
            mobileOnlyLqip={
              variantConfig.isAnchorBlock &&
              (variantConfig.mobileMode === "landscape" ||
                variantConfig.mobileMode === "square_30_26")
                ? image?.crops.lqip?.[variantConfig.mobileMode]
                : image?.crops.lqip?.landscape
            }
            errorSrc={placeHolderImage.src}
          />

          {displayPlayIcon && (
            <svg
              className={styles.playIcon}
              xmlns="http://www.w3.org/2000/svg"
              width="29"
              height="29"
              viewBox="0 0 29 29"
              fill="none"
            >
              <circle cx="14.5106" cy="14.8861" r="14.0545" fill="black" />
              <path
                d="M21.0234 14.8861L11.2538 20.5266L11.2538 9.24563L21.0234 14.8861Z"
                fill="white"
              />
            </svg>
          )}

          {tags && tags.length > 0 && (
            <div className={styles.tagsContainer}>
              {tags.map((tag, index) => (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    if (tag.urlPath) {
                      window.location.href = tag.urlPath;
                    }
                  }}
                  key={index}
                  className={cx(customClass?.tags?.primary)}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          )}
        </a>
      )}

      <div
        className={cx(
          styles.summaryContainer,
          variantConfig.mode === "landscape" &&
            customClass?.latestArticle?.landscape,
        )}
      >
        {!variantConfig.isAnchorBlock ? (
          <SummaryItemSmall
            title={
              descriptionInTitle && description
                ? [
                    `${title} `,
                    <Body key="description" Element="span">
                      {description}
                    </Body>,
                  ]
                : title
            }
            url={path}
            beforeTitle={
              <BreadCrumbs sections={sections} dateElement={dateElement} />
            }
            afterTitle={
              !descriptionInTitle && description && <Body>{description}</Body>
            }
            publishedDate={dateAfter ? publishDate : undefined}
            dateFormat={dateFormat}
            CustomHeadingElement={CustomHeadingElement}
          />
        ) : (
          <div
            className={cx(
              styles.anchorblockSummaryContainer,
              !hideVerticalLine && customClass?.anchorBlock?.summaryItem,
            )}
          >
            <SummaryItemLarge
              beforeTitle={
                <BreadCrumbs sections={sections} dateElement={dateElement} />
              }
              title={title}
              url={path}
              publishedDate={undefined}
              CustomHeadingElement={CustomHeadingElement}
            />
            <div className={styles.anchorblockDivider}>
              <Divider variant="secondary" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Card;
